/**
 * A camera QR reader for the console opened in a phone browser.
 *
 * Nothing starts on mount: the camera opens from the "Start camera" tap, which
 * is the user gesture iPhone Safari wants before it plays a camera stream, and
 * is the moment the browser asks for permission. The rear camera is asked for
 * (`facingMode: 'environment'`); a laptop with only a front camera gets that
 * one. Every animation frame is drawn to an off-screen canvas and decoded with
 * jsQR, and each code read is handed to `onDecode` once (see its doc for the
 * cooldown). The stream is stopped on unmount, on "Stop camera", and when a
 * start is superseded.
 *
 * Every way the camera can fail (refused, absent, busy, not offered on this
 * page, stopped by the phone) renders a WarningCard saying what to do, with a
 * "Try again" that reopens it. Nothing here throws into the page.
 */
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import jsQR from 'jsqr';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { brandColors } from '@liminal/brand/palette';
import { borderRadius } from '@liminal/brand/scale';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Button } from '@liminal/web-ui/primitives/Button';

export interface QrScannerProps {
  /**
   * Called with the text of each QR code read. A code held in view fires
   * once; the same code fires again only after it has gone unread for
   * `cooldownMs` (taken away and shown again). A different code fires at once.
   */
  readonly onDecode: (payload: string) => void;
  /** How long a code must go unread before it can fire again. */
  readonly cooldownMs?: number;
}

const DEFAULT_COOLDOWN_MS = 2000;

const CONSTRAINTS: MediaStreamConstraints = { video: { facingMode: 'environment' }, audio: false };

/**
 * Frames are decoded at no more than this many pixels on the long edge. A
 * phone that hands over 1080p would otherwise spend most of each frame in
 * jsQR; a profile QR held up to the camera is far larger than this needs.
 */
const MAX_DECODE_EDGE = 960;

/** Why the camera is not running, each with its own advice. */
type CameraFailure = 'denied' | 'no-camera' | 'in-use' | 'unsupported' | 'interrupted' | 'failed';

/**
 * getUserMedia rejects with a DOMException named for the cause. A name not
 * listed is a failure we cannot advise on: it reads as `failed`, with the name
 * shown so it can be reported.
 */
const FAILURE_BY_ERROR_NAME: Readonly<Partial<Record<string, CameraFailure>>> = {
  NotAllowedError: 'denied',
  NotFoundError: 'no-camera',
  OverconstrainedError: 'no-camera',
  NotReadableError: 'in-use',
  // A permissions policy or an insecure page withholds the camera itself.
  SecurityError: 'unsupported',
};

const FAILURE_COPY: Readonly<Record<CameraFailure, string>> = {
  denied:
    'Camera access was refused. Allow the camera for this site in the browser (Safari: the aA menu, ' +
    'Website Settings, Camera; Chrome: the icon left of the address, Permissions), then tap Try again.',
  'no-camera': 'No camera was found on this device. Open this page on a phone with a camera.',
  'in-use': 'The camera is in use by another app. Close that app, then tap Try again.',
  unsupported: 'This browser cannot open the camera on this page. Open the console over HTTPS in Safari or Chrome.',
  interrupted: 'The camera stopped, which happens when the phone locks or another app takes it. Tap Try again.',
  failed: 'The camera could not start. Tap Try again, or reload the page.',
};

type ScannerState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'starting' }
  | { readonly kind: 'scanning' }
  | { readonly kind: 'failed'; readonly failure: CameraFailure; readonly errorName: string | null };

/** What a running camera holds: the stream to stop, and the canvas its frames are decoded from. */
interface CameraSession {
  readonly stream: MediaStream;
  readonly context: CanvasRenderingContext2D;
}

function errorName(error: unknown): string {
  if (error instanceof DOMException || error instanceof Error) return error.name;
  return String(error);
}

export function QrScanner({ onDecode, cooldownMs = DEFAULT_COOLDOWN_MS }: QrScannerProps) {
  const [state, setState] = useState<ScannerState>({ kind: 'idle' });
  const videoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<CameraSession | null>(null);
  // Bumped by every stop. A start that finds it changed across an await was
  // superseded (stopped, restarted or unmounted) and must not touch state.
  const attemptRef = useRef(0);

  const stopCamera = useCallback(() => {
    attemptRef.current += 1;
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session !== null) {
      for (const track of session.stream.getTracks()) track.stop();
    }
    const video = videoRef.current;
    if (video !== null) video.srcObject = null;
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const fail = (failure: CameraFailure, name: string | null) => {
    stopCamera();
    setState({ kind: 'failed', failure, errorName: name });
  };

  const start = async () => {
    stopCamera();
    const attempt = attemptRef.current;
    const video = videoRef.current;
    if (video === null) return;

    // Absent on a plain-HTTP page and in browsers with no camera API at all.
    if (navigator.mediaDevices === undefined) {
      fail('unsupported', null);
      return;
    }
    const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (context === null) {
      fail('failed', 'no 2D canvas');
      return;
    }

    setState({ kind: 'starting' });
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
    } catch (error) {
      if (attempt !== attemptRef.current) return;
      const name = errorName(error);
      fail(FAILURE_BY_ERROR_NAME[name] ?? 'failed', name);
      return;
    }
    if (attempt !== attemptRef.current) {
      // Superseded while the browser was asking: this stream is nobody's.
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    sessionRef.current = { stream, context };
    for (const track of stream.getVideoTracks()) {
      track.addEventListener('ended', () => {
        if (attempt === attemptRef.current) fail('interrupted', null);
      });
    }

    video.srcObject = stream;
    try {
      await video.play();
    } catch (error) {
      if (attempt === attemptRef.current) fail('failed', errorName(error));
      return;
    }
    if (attempt !== attemptRef.current) return;
    setState({ kind: 'scanning' });
  };

  const stop = () => {
    stopCamera();
    setState({ kind: 'idle' });
  };

  const decoded = useEffectEvent((payload: string) => onDecode(payload));

  // The decode loop runs exactly while the camera is scanning.
  useEffect(() => {
    const video = videoRef.current;
    const session = sessionRef.current;
    if (state.kind !== 'scanning' || video === null || session === null) return;
    const { context } = session;
    // The last code read and when, for the cooldown. Local to this run of the
    // loop, so a restarted camera reads the code in front of it afresh.
    let last: { readonly payload: string; readonly readAt: number } | null = null;

    const tick = (now: DOMHighResTimeStamp) => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        const scale = Math.min(1, MAX_DECODE_EDGE / Math.max(video.videoWidth, video.videoHeight));
        const width = Math.round(video.videoWidth * scale);
        const height = Math.round(video.videoHeight * scale);
        if (context.canvas.width !== width) context.canvas.width = width;
        if (context.canvas.height !== height) context.canvas.height = height;
        context.drawImage(video, 0, 0, width, height);
        const frame = context.getImageData(0, 0, width, height);
        // The profile QR is dark modules on a light panel, so the inverted
        // pass (half of jsQR's work per frame) is skipped.
        const code = jsQR(frame.data, width, height, { inversionAttempts: 'dontInvert' });
        if (code !== null) {
          const fresh = last === null || last.payload !== code.data || now - last.readAt >= cooldownMs;
          last = { payload: code.data, readAt: now };
          if (fresh) decoded(code.data);
        }
      }
      frameId = requestAnimationFrame(tick);
    };
    let frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [state.kind, cooldownMs]);

  const live = state.kind === 'starting' || state.kind === 'scanning';

  return (
    <Stack spacing={2}>
      {/* Always mounted, so a start has an element to attach the stream to;
          shown only while the camera is opening or running. */}
      <Box
        sx={{
          display: live ? 'block' : 'none',
          position: 'relative',
          width: '100%',
          // Shorter on a phone, so the guest card fits under it. Only the
          // preview is cropped: every frame is decoded whole.
          aspectRatio: { xs: '4 / 3', sm: '1' },
          overflow: 'hidden',
          borderRadius: `${borderRadius.lg}px`,
          backgroundColor: brandColors.black,
        }}
      >
        {/* playsInline and muted: without both, iPhone Safari will not play a
            camera stream inline. */}
        <video
          ref={videoRef}
          aria-label="Camera preview"
          autoPlay
          muted
          playsInline
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {state.kind === 'starting' ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: brandColors.white,
            }}
          >
            <LoadingSpinner color={brandColors.white} text="Opening the camera…" />
          </Box>
        ) : null}
      </Box>

      {state.kind === 'idle' ? (
        <Button onClick={() => void start()} fullWidth>
          Start camera
        </Button>
      ) : null}
      {live ? (
        <Button variant="secondary" onClick={stop} fullWidth>
          Stop camera
        </Button>
      ) : null}
      {state.kind === 'failed' ? (
        <WarningCard tone="error" action={{ label: 'Try again', onClick: () => void start() }}>
          {FAILURE_COPY[state.failure]}
          {state.failure === 'failed' && state.errorName !== null ? ` (${state.errorName})` : null}
        </WarningCard>
      ) : null}
    </Stack>
  );
}
